import { Button, DialogV2, Loader, Typography } from '@recruit-robin/robin-theme/web-components';
import { EmailScheduled, FaceSad } from '@recruit-robin/robin-theme/web-icons';
import { useEffect, useMemo } from 'react';

import {
  InterviewType,
  InterviewTypeSelectionBody,
  useGetNylasConnectionStatusQuery,
  useInterviewTypeSelection,
  useNylasCalendarInterviewType,
} from '@/features/nylas-calendar-manage-connection-modal';
import { useCalendarSchedulingLinkQuery } from '@/hooks/shared/useCalendarSchedulingLinkQuery';
import { useDictionary, useLanguage } from '@/shared/lib/i18n';
import { RobinLogoSkeleton } from '@/shared/ui/robin-logo-skeleton';
import { useCampaignStore } from '@/stores/campaign/campaign.store';
import { GlobalDialogType } from '@/stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from '@/stores/global-dialogs/hooks/useGlobalDialog';
import { useGlobalDialogData } from '@/stores/global-dialogs/hooks/useGlobalDialogData';
import { useIsGlobalDialogOpen } from '@/stores/global-dialogs/hooks/useIsGlobalDialogOpen';
import { NylasScheduling } from '@nylas/react';
import { useStyles } from './ReachoutInterviewModal.styles';
import { useCandidateStore } from '@/stores/candidate/candidate.store';

const NylasCalendar = ({
  nylasCalendarConfig,
  onInterviewBooked,
}: {
  nylasCalendarConfig: NylasCalendarConfig;
  onInterviewBooked?: () => Promise<void> | void;
}) => {
  const classes = useStyles();
  const dictionary = useDictionary();
  const language = useLanguage();

  const { canRender, data, themeConfig } = nylasCalendarConfig;

  return (
    <div key={data.schedulerLink} css={classes.nylasCalendarContainer}>
      {canRender ? (
        <NylasScheduling
          css={classes.nylasCalendar}
          clientId={data.clientId}
          slug={data.slug}
          schedulerApiUrl={data.schedulerApiUrl}
          themeConfig={themeConfig}
          onBookedEventInfo={() => onInterviewBooked?.()}
          bookingInfo={{
            language: language,
            primaryParticipant: { name: data.name, email: data.email },
            additionalFields: {
              identifier: { value: data.identifier, type: 'metadata' },
              phone: { value: data.phone, type: 'phone_number' },
              job_title: { value: data.jobTitle, type: 'text' },
              scheduler_type: { value: data.schedulerType, type: 'metadata' },
            },
          }}
        />
      ) : (
        <div css={classes.errorContainer}>
          <FaceSad size={128} color={(c) => c.neutral.variant[30]} />

          <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
            {dictionary.somethingWentWrong}
          </Typography>
        </div>
      )}
    </div>
  );
};

const Content = ({
  esPersonId,
  onInterviewBooked,
}: {
  esPersonId: string;
  onInterviewBooked?: () => Promise<void> | void;
}) => {
  const dictionary = useDictionary();
  const classes = useStyles();
  const reachoutInterviewModal = useGlobalDialog(GlobalDialogType.ReachoutInterviewModal);
  const interviewType = useNylasCalendarInterviewType();

  const {
    selectedInterviewType,
    setSelectedInterviewType,
    interviewValue,
    setInterviewValue,
    errors,
    schedulerConfig,
    handleValidateAndSave,
    isSaving,
  } = useInterviewTypeSelection({ initialInterviewType: interviewType, onConfirm: () => {} });

  const { data: nylasConnectionStatus } = useGetNylasConnectionStatusQuery();
  const showOnlineOption = nylasConnectionStatus?.calendar?.has_online_meetings_authorization !== false;

  useEffect(() => {
    if (!showOnlineOption && selectedInterviewType === InterviewType.Online) {
      setSelectedInterviewType(InterviewType.Call);
    }
  }, [showOnlineOption, selectedInterviewType, setSelectedInterviewType]);

  const connectedAts = useCampaignStore((s) => s.campaign?.connected_ats);
  const vacancyName = useCampaignStore((s) => s.campaign?.name);

  const campaignIdFromCandidate = useCandidateStore((s) => s.selectedCandidate?.campaignId);
  const campaignIdFromStore = useCampaignStore((s) => s.campaign?.id);
  const campaignId = campaignIdFromStore ?? campaignIdFromCandidate;

  const jobTitle = useMemo(() => {
    return connectedAts?.job_title || vacancyName || '';
  }, [connectedAts?.job_title, vacancyName]);

  const jobImprovedId = connectedAts?.db_id !== null ? Number(connectedAts?.db_id) : undefined;

  const {
    data: schedulerLink,
    isFetching: isConfigLoading,
    status: configStatus,
  } = useCalendarSchedulingLinkQuery({
    esPersonId,
    campaignId,
    interviewType: selectedInterviewType,
    jobTitle,
    jobImprovedId,
    autoFillJobContext: true,
  });

  const nylasCalendarData = useNylasCalendarConfig({ schedulerLink: schedulerLink?.link });
  const shouldShowFields = !schedulerLink?.link && !isConfigLoading;

  return (
    <DialogV2.Content customCss={classes.content}>
      <DialogV2.Close onRequestClose={() => reachoutInterviewModal.close()} />

      <DialogV2.Header customCss={classes.header}>
        <div css={classes.headerTitle}>
          <EmailScheduled size={24} />
          <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[90]}>
            {dictionary.scheduleInterviewNow}
          </Typography>
        </div>

        <div css={classes.headerDescription}>
          <Typography variant={(v) => v.heading[5]} color={(c) => c.neutral.variant[70]}>
            {dictionary.youCanManageEmailSettings}
          </Typography>
        </div>
      </DialogV2.Header>

      <DialogV2.Body customCss={classes.contentBody}>
        <InterviewTypeSelectionBody
          selectedInterviewType={selectedInterviewType}
          onSelectedInterviewType={setSelectedInterviewType}
          interviewValue={shouldShowFields ? interviewValue : undefined}
          setInterviewValue={shouldShowFields ? setInterviewValue : undefined}
          error={errors}
          schedulerConfig={schedulerConfig}
          showOnlineOption={showOnlineOption}
        />

        {shouldShowFields && (
          <div css={classes.saveButtonContainer}>
            <Button
              style="filled"
              type="highlight"
              onClick={() => handleValidateAndSave()}
              label={dictionary.save}
              startIcon={isSaving && <Loader size={18} color={(c) => c.primary.fuchsia} />}
            />
          </div>
        )}

        {isConfigLoading && <RobinLogoSkeleton position="center" />}

        {!isConfigLoading && configStatus === 'success' && schedulerLink?.link && (
          <NylasCalendar nylasCalendarConfig={nylasCalendarData} onInterviewBooked={onInterviewBooked} />
        )}
      </DialogV2.Body>
    </DialogV2.Content>
  );
};

const ContentWrapper = ({
  esPersonId,
  onInterviewBooked,
}: {
  esPersonId: string | undefined;
  onInterviewBooked?: () => Promise<void> | void;
}) => {
  if (!esPersonId) {
    return null;
  }

  return <Content esPersonId={esPersonId} onInterviewBooked={onInterviewBooked} />;
};

export const ReachoutInterviewModal = () => {
  const classes = useStyles();
  const isVisible = useIsGlobalDialogOpen(GlobalDialogType.ReachoutInterviewModal);
  const dialogData = useGlobalDialogData({
    isVisible,
    dialogType: GlobalDialogType.ReachoutInterviewModal,
  });

  return (
    <DialogV2.Root isVisible={isVisible} customCss={classes.root} variant="right" id="reachout-interview-modal">
      <ContentWrapper esPersonId={dialogData?.esPersonId} onInterviewBooked={dialogData?.onInterviewBooked} />
    </DialogV2.Root>
  );
};

const useNylasCalendarConfig = ({ schedulerLink }: { schedulerLink: string | undefined | null }) => {
  const data = useMemo(() => {
    const emptyState = {
      clientId: '',
      slug: '',
      identifier: '',
      name: '',
      email: '',
      schedulerApiUrl: '',
      phone: '',
      schedulerLink: '',
      jobTitle: '',
      schedulerType: '',
    };

    if (!schedulerLink) return emptyState;

    try {
      const url = new URL(schedulerLink);
      const [region, clientId, slug] = url.pathname.split('/').filter(Boolean);

      const searchParams = new URLSearchParams(url.search);
      const identifier = searchParams.get('identifier') || '';
      const name = searchParams.get('name') || '';
      const email = searchParams.get('email') || '';
      const phone = searchParams.get('phone') || '';
      const jobTitle = searchParams.get('job_title') || '';
      const schedulerType = searchParams.get('scheduler_type') || '';

      const normalizedRegion = region?.toLowerCase() || 'eu';
      const schedulerApiUrl = normalizedRegion ? `https://api.${normalizedRegion}.nylas.com` : '';

      return {
        clientId,
        slug,
        identifier,
        name,
        email,
        schedulerApiUrl,
        phone,
        jobTitle,
        schedulerLink,
        schedulerType,
      };
    } catch {
      return emptyState;
    }
  }, [schedulerLink]);

  const canRenderScheduler = Boolean(schedulerLink && data.clientId && data.slug && data.schedulerApiUrl);

  const themeConfig = {
    '--nylas-primary': '#E56CBF',
    '--nylas-base-500': '#C80E8D',
    '--nylas-base-600': '#C80E8D',
  } as const;

  return { canRender: canRenderScheduler, data, themeConfig };
};

type NylasCalendarConfig = ReturnType<typeof useNylasCalendarConfig>;
